// @ts-strict-ignore
import React, { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Combobox, Input, SingleDate, Tooltip } from '@node-space/ui-components'
import { FormFieldsRow } from 'components/Form/FormFieldsRow'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { CountryCodes, ErrorDetails } from 'types/types'
import { INDIVIDUAL_CUSTOMER_DEFAULT_FORM_DATA } from '../helpers/form'
import { ConfirmIndividualCustomerMethod, IndividualCustomerFormData } from '../helpers/types'
import { scrollToTopError } from '../helpers/utils'
import { useIndividualCustomerForm } from '../hooks/useIndividualCustomerForm'
import { AddCustomerActions } from './AddCustomerActions'

export interface AddIndividualCustomerProps {
  defaultValues: IndividualCustomerFormData
  onConfirm: ConfirmIndividualCustomerMethod
  onCancel: () => void
  fieldErrors?: ErrorDetails
}

export const AddIndividualCustomer = ({
  defaultValues,
  onConfirm,
  onCancel,
  fieldErrors,
}: AddIndividualCustomerProps) => {
  const { t } = useTranslation()
  const { enableOptionalEpcFields } = useFeatureFlags()
  const { accountCapabilities } = useAccountsContext()
  const paxosUsStates = accountCapabilities?.paxosUsStatesCapability || []

  const { formFields, formValues, displayData, hasErrors, handleSubmit, form } =
    useIndividualCustomerForm(
      defaultValues ?? INDIVIDUAL_CUSTOMER_DEFAULT_FORM_DATA,
      fieldErrors,
      paxosUsStates
    )

  const getConfirmData = (originalData: IndividualCustomerFormData) => {
    const confirmData = { ...originalData }
    if (formValues?.countryCode !== CountryCodes.US) {
      confirmData.stateCode = undefined
    }
    if (!paxosUsStates?.length || !paxosUsStates?.includes(formValues?.stateCode)) {
      confirmData.estimatedYearlyIncome = undefined
      confirmData.employmentIndustrySector = undefined
    }
    return confirmData
  }

  const onSubmit = (formValues: IndividualCustomerFormData) => {
    const confirmData = getConfirmData(formValues)
    const confirmDisplayData = getConfirmData(displayData as IndividualCustomerFormData)
    onConfirm({ data: confirmData, displayData: confirmDisplayData })
  }

  const topAnchorRef = useRef<HTMLDivElement>(null)
  const fieldRefs = useRef<Record<string, HTMLElement | null>>({})

  const showPaxosUsStatesFields = useMemo(
    () =>
      !!formValues?.stateCode &&
      !!paxosUsStates?.length &&
      paxosUsStates?.includes(formValues?.stateCode),
    [formValues?.stateCode, paxosUsStates]
  )

  const onInvalid = () => {
    scrollToTopError(form, fieldRefs)
  }

  useEffect(() => {
    if (!fieldErrors || !hasErrors) return
    scrollToTopError(form, fieldRefs)
  }, [fieldErrors, hasErrors])

  return (
    <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
      <Box ref={topAnchorRef} />
      <Box flexCol gapY={16}>
        <FormFieldsRow
          leftField={
            <Box
              ref={el => {
                fieldRefs.current.firstName = el
              }}
            >
              <Input {...formFields?.firstName} />
            </Box>
          }
          rightField={
            <Box
              ref={el => {
                fieldRefs.current.lastName = el
              }}
            >
              <Input {...formFields?.lastName} />
            </Box>
          }
        />
        <Box
          ref={el => {
            fieldRefs.current.dateOfBirth = el
          }}
        >
          <SingleDate {...formFields?.dateOfBirth} />
        </Box>
        <Box
          ref={el => {
            fieldRefs.current.nationality = el
          }}
        >
          <Combobox {...formFields?.nationality} />
        </Box>
        <Box
          ref={el => {
            fieldRefs.current.emailAddress = el
          }}
        >
          <Input {...formFields?.emailAddress} />
        </Box>

        <Box
          ref={el => {
            fieldRefs.current.description = el
          }}
        >
          <Input
            {...formFields?.description}
            testId="individual-description"
            Tooltip={
              <Tooltip
                type="complex"
                bodyContent={t('embeddedCustomers.individualFormTooltip.customerDescription')}
              />
            }
          />
        </Box>
        <h3 className="text-h3 text-foreground-prominent pt-2">
          {t('customers.residentialAddress')}
        </h3>
        <Box
          ref={el => {
            fieldRefs.current.addressLine1 = el
          }}
        >
          <Input
            {...formFields?.addressLine1}
            {...(enableOptionalEpcFields && { optionalText: t('optional') })}
          />
        </Box>
        <Box
          ref={el => {
            fieldRefs.current.addressLine2 = el
          }}
        >
          <Input {...formFields?.addressLine2} optionalText={t('optional')} />
        </Box>
        <FormFieldsRow
          leftField={
            <Box
              ref={el => {
                fieldRefs.current.city = el
              }}
            >
              <Input
                {...formFields?.city}
                {...(enableOptionalEpcFields && { optionalText: t('optional') })}
              />
            </Box>
          }
          rightField={
            <Box
              ref={el => {
                fieldRefs.current.postalCode = el
              }}
            >
              <Input
                {...formFields?.postalCode}
                {...(enableOptionalEpcFields && { optionalText: t('optional') })}
              />
            </Box>
          }
        />
        <Box
          ref={el => {
            fieldRefs.current.countryCode = el
          }}
        >
          <Combobox {...formFields?.countryCode} />
        </Box>
        {formValues?.countryCode === CountryCodes.US && (
          <Box
            ref={el => {
              fieldRefs.current.stateCode = el
            }}
          >
            <Combobox {...formFields?.stateCode} />
          </Box>
        )}
        <h3 className="text-h3 text-foreground-prominent pt-2">
          {t('customers.complianceInformation')}
        </h3>

        <FormFieldsRow
          leftField={
            <Box
              ref={el => {
                fieldRefs.current.taxIdentificationNumber = el
              }}
            >
              <Input
                {...formFields?.taxIdentificationNumber}
                placeholder={t('customers.taxIdentificationPlaceholder')}
                Tooltip={
                  <Tooltip type="complex" bodyContent={t('customers.taxIdentificationTooltip')} />
                }
              />
            </Box>
          }
          rightField={
            <Box className="pt-6">
              <Box
                ref={el => {
                  fieldRefs.current.taxResidenceCountryCode = el
                }}
              >
                <Combobox {...formFields?.taxResidenceCountryCode} label="" />
              </Box>
            </Box>
          }
        />
        <Box
          ref={el => {
            fieldRefs.current.employmentStatus = el
          }}
        >
          <Combobox
            {...formFields?.employmentStatus}
            Tooltip={
              <Tooltip type="complex" bodyContent={t('customers.employmentStatusTooltip')} />
            }
          />
        </Box>
        <Box
          ref={el => {
            fieldRefs.current.sourceOfFunds = el
          }}
        >
          <Combobox
            {...formFields?.sourceOfFunds}
            Tooltip={<Tooltip type="complex" bodyContent={t('customers.sourceOfFundsTooltip')} />}
          />
        </Box>
        <Box
          ref={el => {
            fieldRefs.current.pepStatus = el
          }}
        >
          <Combobox
            {...formFields?.pepStatus}
            Tooltip={<Tooltip type="complex" bodyContent={t('customers.pepStatusTooltip')} />}
          />
        </Box>
        <Box flex gapX={12}>
          <Box flexSize="fill">
            <Box
              ref={el => {
                fieldRefs.current.expectedMonthlyVolumeAmount = el
              }}
            >
              <Input
                {...formFields?.expectedMonthlyVolumeAmount}
                Tooltip={
                  <Tooltip type="complex" bodyContent={t('customers.monthlyVolumesTooltip')} />
                }
              />
            </Box>
          </Box>
          <Box className="pt-5 w-[148px]">
            <Box
              ref={el => {
                fieldRefs.current.expectedMonthlyVolumeCurrency = el
              }}
            >
              <Combobox {...formFields?.expectedMonthlyVolumeCurrency} label="" />
            </Box>
          </Box>
        </Box>
        <Box
          ref={el => {
            fieldRefs.current.useCase = el
          }}
        >
          <Combobox
            {...formFields?.useCase}
            Tooltip={<Tooltip type="complex" bodyContent={t('customers.serviceTypeTooltip')} />}
          />
        </Box>
        <Box
          ref={el => {
            fieldRefs.current.intendedUseOfAccount = el
          }}
        >
          <Combobox
            {...formFields?.intendedUseOfAccount}
            Tooltip={<Tooltip type="complex" bodyContent={t('customers.intendedUseTooltip')} />}
          />
        </Box>
        {showPaxosUsStatesFields && (
          <>
            <Box
              ref={el => {
                fieldRefs.current.estimatedYearlyIncome = el
              }}
            >
              <Combobox {...formFields?.estimatedYearlyIncome} />
            </Box>
            <Box
              ref={el => {
                fieldRefs.current.employmentIndustrySector = el
              }}
            >
              <Combobox {...formFields?.employmentIndustrySector} />
            </Box>
          </>
        )}
        <Box
          ref={el => {
            fieldRefs.current.externalReference = el
          }}
        >
          <Input
            {...formFields?.externalReference}
            testId="individual-external-reference"
            optionalText={t('optional')}
            Tooltip={
              <Tooltip type="complex" bodyContent={t('customers.externalReferenceTooltip')} />
            }
          />
        </Box>
      </Box>

      <AddCustomerActions onCancel={onCancel} isDisableContinue={hasErrors} />
    </form>
  )
}
